import { useEffect, useMemo, useRef, useState } from "react";
import { Table, Flex, Form, Spin } from "@/base/components/index";
import { DataTableActions } from "./data-table-actions";
import { useContextDataTable } from "../context/index";
import { usePageTableStyles } from "./data-table.styles";
import { DataTableEmpty } from "./data-table-empty";
import { DataTableHeader } from "./data-table-header";
import { layoutPrefixCls } from "@/base/components/index";

export const DataTable = () => {
  const { styles } = usePageTableStyles();
  const { loading, columns, rowKey, tableProps, controller, formTable } =
    useContextDataTable();

  const { dataSource, dataSourceTotal, pagination, setPagination } = controller;

  const [headerHeight, setHeaderHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const scrollSetting: any = useMemo(
    () => ({
      x:
        columns?.reduce((acc: number, column: any) => {
          return acc + (column.minWidth || column.with || 0);
        }, 0) || undefined,
    }),
    [columns],
  );

  const paginationSetting = useMemo(
    () =>
      pagination.total > dataSource.length && {
        ...pagination,
      },
    [
      pagination.current,
      pagination.pageSize,
      pagination.total,
      dataSource.length,
    ],
  );

  const handleChangeTableSettings = (
    pagination: any,
    _: any,
    ___: any,
    { action }: any,
  ) => {
    if (action === "paginate") {
      setPagination(pagination.current, pagination.pageSize);
    }
  };

  useEffect(() => {
    const parentElement = document.querySelector(
      `header.${layoutPrefixCls}-header`,
    );
    if (parentElement) {
      observerRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          setHeaderHeight(height);
        }
      });

      observerRef.current.observe(parentElement);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      const tableEl = tableRef.current;
      tableEl.style.setProperty("--page-header-height", `${headerHeight}px`);
    }
  }, [headerHeight, tableRef.current]);

  return (
    <Form size="large" form={formTable} layout="vertical" autoComplete="off">
      <Flex vertical gap={24} align="stretch">
        {!!dataSourceTotal && <DataTableActions />}

        {!!dataSourceTotal && <DataTableHeader />}

        <Spin wrapperClassName={styles.spin} spinning={loading} size="large">
          {dataSourceTotal === 0 && !loading && <DataTableEmpty />}
          <div ref={tableRef}>
            {!!dataSourceTotal && (
              <Table
                sticky
                tableLayout="auto"
                scroll={scrollSetting}
                onChange={handleChangeTableSettings}
                pagination={paginationSetting}
                rowKey={rowKey || "Id"}
                columns={columns}
                dataSource={dataSource}
                className={styles.table}
                {...tableProps}
              />
            )}
          </div>
        </Spin>
      </Flex>
    </Form>
  );
};
